/**
 * Houseplant v2 — Centre-slot carousel
 *
 * A pagination row whose ACTIVE ITEM ALWAYS HOLDS THE CENTRE SLOT, above a stack
 * of panels that crossfade in one shared grid cell. Advancing rotates the row
 * rather than moving a highlight along it — every item shifts one place, and the
 * one falling off the start wraps round to the end.
 *
 * ── Why this exists as its own file ─────────────────────────────────────────
 * It was `hp-press-quotes.js`, whose own header ended: "Two components now share
 * that contract — worth extracting to a base class if a third appears." The
 * testimonials carousel is that third, and it needs not just the autoplay
 * contract but the whole mechanism, so the element was generalised in place
 * rather than copied. Nothing here is press- or testimonial-specific.
 *
 * ── The markup contract ─────────────────────────────────────────────────────
 * Parts are found by DATA ATTRIBUTE, never by class name, so each consumer keeps
 * its own BEM namespace and its own styling:
 *
 *   <hp-centre-carousel data-autoplay="true" data-autoplay-speed="6">
 *     [data-carousel-prev]    optional — removed automatically if < 2 slides
 *     [data-carousel-item]    the pagination items, in source order
 *     [data-carousel-next]    optional
 *     [data-carousel-panel]   the content, all but the opening one `hidden`
 *
 * BOTH items and panels carry `data-index`, and panels are matched BY that index
 * rather than by their position in the list — so one slide may own SEVERAL
 * panels. The 50/50 section needs exactly that: its copy and its image sit in
 * different columns and cannot be one element, but they change together. A
 * consumer with one panel per slide is just the single-panel case of the same
 * rule, so nothing changed for the press or testimonial carousels.
 *
 * The opening slide is the one Liquid rendered without `hidden`, read rather
 * than assumed — consumers open on the MIDDLE item, and duplicating that
 * arithmetic here would be one more thing to keep in sync.
 *
 * ── data-carousel-rotate="false" ────────────────────────────────────────────
 * Turns OFF the centre-slot rotation, for a consumer whose pagination is a row
 * of bars rather than the content itself. Reordering bars would be meaningless
 * movement, and FLIP-animating them actively wrong. Everything else — the
 * crossfade, the autoplay contract, the editor plumbing — is identical, which is
 * the whole reason this is a flag rather than a second element.
 *
 * ── Two contracts the STYLESHEET has to hold up ─────────────────────────────
 * 1. `[data-carousel-panel][hidden]` must keep `display` (grid/flex), so every
 *    panel keeps its cell and the stage stays as tall as the longest one.
 *    `visibility: hidden` does the real work of removing inactive panels from
 *    the accessibility tree and tab order. Because nothing changes `display`,
 *    both panels always have a painted state and the crossfade needs no frame
 *    juggling — see show().
 * 2. Items must transition `transform` and `opacity` only. The FLIP below hands
 *    control back to the stylesheet by clearing inline styles, so anything else
 *    animating there will fight it.
 *
 * Autoplay follows the same contract as the hero slider (assets/hp-hero-slider.js):
 * pause on hover, on keyboard focus, and when the tab is hidden; stop for good
 * once the visitor picks a panel; skip entirely under prefers-reduced-motion.
 */
/**
 * How far the pointer must travel before a drag counts as a swipe rather than a
 * click that wobbled. 40px is comfortably past an accidental shift while still
 * well inside a deliberate flick.
 */
const DRAG_THRESHOLD = 40;

/**
 * Trackpad swipe. `deltaX` arrives as a long stream of small values, so it is
 * accumulated to a threshold rather than acted on per event, and then LOCKED
 * until the stream goes quiet — a flick keeps emitting momentum deltas for
 * several hundred ms, and without the lock one gesture runs through the whole
 * carousel. WHEEL_IDLE is the silence that ends a gesture.
 */
const WHEEL_THRESHOLD = 50;
const WHEEL_IDLE = 200;

class HpCentreCarousel extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-carousel-item]'));
    this.panels = Array.from(this.querySelectorAll('[data-carousel-panel]'));
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.rotates = this.dataset.carouselRotate !== 'false';

    // The slide COUNT is the number of pagination items, not of panels — a slide
    // may own several panels (see the header).
    this.count = this.items.length;

    if (this.count < 2) {
      // One panel needs no controls — leaving dead arrows on the page is worse
      // than having none.
      this.prev?.remove();
      this.next?.remove();
      if (this.panels.length) this.items[0]?.setAttribute('aria-current', 'true');
      return;
    }

    // Read the opening index from the markup rather than assuming 0 — Liquid
    // starts the carousel on the middle item.
    const opening = this.panels.find((p) => !p.hidden);
    this.index = opening ? Number(opening.dataset.index) || 0 : 0;
    // Slot the active item should occupy — the middle one.
    this.centreSlot = Math.floor(this.items.length / 2);

    /**
     * HOW MANY SLOTS EITHER SIDE OF CENTRE STAY VISIBLE. 1 shows three items —
     * the active one flanked by its neighbours, which is what the press node
     * draws at 390. It is a data attribute because the right number depends on
     * how WIDE the items are, and that is content, not design: the node's five
     * logos measure 62-137 and fit 1312 comfortably, while a real press row of
     * seven wordmarks does not. A consumer that wants every item visible sets a
     * number larger than its item count.
     */
    this.window = parseInt(this.dataset.carouselWindow, 10);
    if (Number.isNaN(this.window)) this.window = 1;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.items.forEach((item) =>
      item.addEventListener('click', () => {
        this.stopAutoplay(true);
        this.show(Number(item.dataset.index));
      })
    );

    this.prev?.addEventListener('click', () => {
      this.stopAutoplay(true);
      this.show(this.index - 1);
    });
    this.next?.addEventListener('click', () => {
      this.stopAutoplay(true);
      this.show(this.index + 1);
    });

    this.addEventListener('pointerenter', () => this.pauseAutoplay());
    this.addEventListener('pointerleave', () => this.resumeAutoplay());
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resumeAutoplay();
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.initDrag();
    this.initWheel();

    // Establish the opening arrangement without animating it.
    this.positionItems(false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay(true);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /* ------------------------------------------------------------------ show */

  /** Wraps in both directions, so the arrows never dead-end. */
  show(next) {
    const count = this.count;
    const index = ((next % count) + count) % count;
    if (index === this.index) return;

    // Both toggled SYNCHRONOUSLY. An earlier version deferred the hide to a
    // requestAnimationFrame, which is a correctness bug: rAF is throttled in a
    // background tab, so the outgoing panel stayed on screen and they stacked up
    // — four visible at once. No frame dance is needed anyway, because `[hidden]`
    // here keeps its display (see the stylesheet contract above); nothing changes
    // display, so both elements already have a painted state to transition from.
    this.panels.forEach((panel) => {
      panel.hidden = Number(panel.dataset.index) !== index;
    });

    this.items.forEach((item, i) => {
      // Removed rather than set to "false": aria-current="false" is still an
      // announced state in some screen readers.
      if (i === index) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });

    this.index = index;
    this.positionItems(true);
  }

  /**
   * Rotate the row so the active item sits in the centre slot.
   *
   * Uses flex `order`, not DOM moves: the tab order, the markup and every
   * listener stay exactly as rendered, and Liquid can produce the correct
   * opening arrangement on its own — so this still looks right with JS off.
   *
   * The movement is FLIP-animated (First-Last-Invert-Play): measure, reorder,
   * measure again, then jump each item back to where it was and let it
   * transition to zero. Deliberately driven by a forced reflow rather than a
   * requestAnimationFrame — rAF is throttled in a background tab, which would
   * strand the items mid-transform. Same reasoning as the panel swap above.
   */
  /**
   * Slide a clipping track so the active item is dead centre in it. No-op unless
   * the consumer marks a track — see the call site for why this exists.
   */
  centreTrack() {
    /**
     * TWO ELEMENTS, and the split is the whole point.
     *
     * The TRACK clips and must never be transformed: `transform` moves an
     * element's clip rectangle along with its content, so translating the
     * `overflow: hidden` box slides the WINDOW rather than sliding content
     * inside a fixed window. This shipped that way and the symptom was
     * unmistakable once real-width logos were in — the first logo painted at
     * left -101, outside the viewport and past the prev arrow, with 277px of
     * dead space before the next one. Content escaping one side while a gap
     * opens on the other is the signature of a moving clip.
     *
     * So the track holds still and the BELT inside it moves.
     */
    const track = this.querySelector('[data-carousel-track]');
    if (!track) return;
    const belt = this.querySelector('[data-carousel-belt]') || track.firstElementChild;
    if (!belt) return;
    const active = this.items[this.index];
    if (!active) return;

    /* Measure untransformed, or each pass compounds the last one's offset. */
    belt.style.transform = 'none';
    /* The reference is the TRACK's box — the fixed window — not the belt's,
       which is the thing being moved into place against it. */
    const trackBox = track.getBoundingClientRect();
    const activeBox = active.getBoundingClientRect();
    if (!activeBox.width) return; // hidden item, nothing meaningful to centre

    const delta =
      trackBox.left + trackBox.width / 2 - (activeBox.left + activeBox.width / 2);
    belt.style.transform = `translateX(${delta}px)`;
  }

  positionItems(animate) {
    const count = this.items.length;
    const moving = animate && this.rotates && !this.reducedMotion.matches;

    // Bar pagination: the items are chrome, not content, so there is nothing to
    // rotate. aria-current (set in show()) is the whole of their state.
    if (!this.rotates) return;

    // FIRST
    const before = moving ? this.items.map((el) => el.getBoundingClientRect().left) : null;
    const previousSlots = this.slots;
    // Which items were OUTSIDE the narrow three-slot window, and so had no box
    // to measure. Captured before the reorder rewrites the attribute.
    const wasFar = this.items.map((el) => el.hasAttribute('data-far'));

    // Reorder. `order` is per-slot, so slot 0 is the leftmost item.
    this.slots = this.items.map((item, i) => {
      const slot = (((i - this.index + this.centreSlot) % count) + count) % count;
      item.style.order = slot;
      /**
       * `data-far` marks the items more than one slot from the centre, which is
       * everything a narrow row hides — the press node (9120:4879) shows exactly
       * three, the active one flanked by its immediate neighbours. Set as a
       * positive flag on the ones to HIDE rather than on the ones to show, so a
       * JS-off page still renders every item instead of none. A consumer that
       * wants all of them at every width simply never styles this attribute.
       */
      item.toggleAttribute('data-far', Math.abs(slot - this.centreSlot) > this.window);
      return slot;
    });

    /**
     * OPTIONAL: centre the active item inside a clipping track.
     *
     * Only runs for a carousel that marks one — the press row, whose arrows are
     * pinned to the page width with the logos clipped between them. Everything
     * else is untouched.
     *
     * `justify-content: center` on the track centres the SET, not the active
     * item, and those are only the same thing when every item is the same width.
     * Press logos are not: the design's run 62-137 wide on a common cap height,
     * so the centre slot can sit well off the row's centre. This measures the
     * two and closes the difference exactly.
     *
     * Deliberately INSTANT — no transition on the track. The FLIP pass below
     * measures each item AFTER this runs, so the translate is already folded
     * into `after`, and each item animates from its true old position to its
     * true new one. Transitioning the track as well would move everything twice.
     */
    this.centreTrack();

    if (!moving || !previousSlots) return;

    // LAST + INVERT
    const after = this.items.map((el) => el.getBoundingClientRect().left);

    this.items.forEach((item, i) => {
      /**
       * One item per step doesn't shift by a slot — it WRAPS, from one end of
       * the row to the other. Animating that sends it sweeping back across the
       * whole row against the traffic, which is the opposite of the infinite
       * belt this is meant to read as.
       *
       * So the wrapper is never inverted: it simply appears at its new end, the
       * way an item entering a marquee does, and fades up from nothing to cover
       * the pop. Everything else slides one place as normal.
       */
      const moved = Math.abs(this.slots[i] - previousSlots[i]);
      const wrapped = moved > 1;

      /**
       * NARROW ROWS: an item crossing into or out of the three-slot window has
       * no box on one side of the measurement — `display: none` reports left = 0.
       * FLIP would read that as "it was at the viewport edge" and send it
       * sweeping across the row, which is the same wrong movement the wrap case
       * avoids, so it takes the same escape: appear in place and fade up.
       */
      const crossedWindow = wasFar[i] !== item.hasAttribute('data-far');

      if (wrapped || crossedWindow) {
        item.style.transition = 'none';
        item.style.opacity = '0';
        return;
      }

      const dx = before[i] - after[i];
      if (!dx) return;
      item.style.transition = 'none';
      item.style.transform = `translateX(${dx}px)`;
    });

    // Force a reflow so the inverted position is the painted starting state.
    void this.offsetWidth;

    // PLAY — clearing hands control back to the stylesheet's transitions, so the
    // sliders travel to zero and the wrapper fades up to its resting opacity.
    this.items.forEach((item) => {
      item.style.transition = '';
      item.style.transform = '';
      item.style.opacity = '';
    });
  }

  /* ------------------------------------------------------------------ drag */

  /**
   * SWIPE, AND MOUSE-DRAG, to change slide.
   *
   * The hero slider gets this free: its track is a native scroll-snap container,
   * so the browser owns the swipe, the momentum and the snapping. This carousel
   * cannot be one. Its panels CROSSFADE in a single shared grid cell, and one
   * slide may own several panels sitting in different columns — the 50/50's copy
   * and its image — so there is no scrollable track to drag. The gesture has to
   * be synthesised, and it resolves to the same show() the arrows call.
   *
   * DELIBERATELY NOT LIVE-TRACKED. A scroll container follows the finger; this
   * commits once past a threshold, because the panels have no travel to follow
   * it with — they occupy one cell and fade between states. Giving them travel
   * would mean giving up the shared-cell crossfade, which is the thing that lets
   * a slide own panels in two different columns.
   *
   * `touch-action` is set HERE rather than in a stylesheet because three
   * consumers have three stylesheets and the property belongs to this behaviour,
   * not to any one of their designs: `pan-y` hands the vertical axis back to the
   * browser so the page still scrolls under a finger, while we keep horizontal.
   */
  initDrag() {
    this.style.touchAction = 'pan-y';
    this.drag = null;
    this.dragged = false;

    this.addEventListener('pointerdown', (event) => {
      // Reset BEFORE the early returns below, or a gesture that never produced
      // a click leaves the flag set and swallows the next unrelated one.
      this.dragged = false;
      this.drag = null;
      if (event.button !== 0) return;
      /**
       * The controls own their own clicks, and the pagination row REORDERS
       * under the pointer whenever the slide changes — so a drag begun on a bar
       * is aiming at a moving target. Native form fields keep their own
       * selection behaviour for the same reason: the 50/50 can carry a product
       * form.
       */
      if (
        event.target.closest(
          '[data-carousel-item], [data-carousel-prev], [data-carousel-next], input, textarea, select'
        )
      ) {
        return;
      }
      this.drag = { x: event.clientX, y: event.clientY, id: event.pointerId };
    });

    this.addEventListener('pointermove', (event) => {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const dx = event.clientX - this.drag.x;
      const dy = event.clientY - this.drag.y;
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      // Past the threshold but travelling mostly downward: that is a page
      // scroll the visitor started inside the carousel, not a swipe. Abandon it
      // rather than stealing the gesture.
      if (Math.abs(dx) <= Math.abs(dy)) {
        this.drag = null;
        return;
      }
      this.dragged = true;
      this.stopAutoplay(true);
      /**
       * THE MOUSE HAS ALREADY BEEN SELECTING TEXT ALL THE WAY HERE, and that is
       * what made the gesture feel broken rather than absent: the slide changed
       * AND the visitor was left holding a blue smear across the copy, so it
       * read as a failed text-selection. Measured on a real drag across the
       * airbnb 50/50: 211 characters selected.
       *
       * Cleared and then suppressed for the REST of the gesture, rather than
       * disabling selection on the element outright — a drag shorter than the
       * threshold still selects normally, so someone copying a product name or
       * a line of copy is unaffected. Only a committed swipe takes the axis.
       */
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) selection.removeAllRanges();
      this.style.userSelect = 'none';
      this.style.webkitUserSelect = 'none';
      // Dragging LEFT pulls the next slide in, the way a belt moves under a
      // finger.
      this.show(this.index + (dx < 0 ? 1 : -1));
      // One slide per gesture: stop tracking until the pointer lifts, so a long
      // drag doesn't run through the whole carousel.
      this.drag = null;
    });

    const endDrag = () => {
      this.drag = null;
      // Hand the axis back — see the note above.
      this.style.userSelect = '';
      this.style.webkitUserSelect = '';
    };
    this.addEventListener('pointerup', endDrag);
    this.addEventListener('pointercancel', endDrag);

    /**
     * NATIVE IMAGE DRAG-AND-DROP, the other half of the same problem. `<img>` is
     * `draggable` by default, so a swipe begun on the photo tore a ghost image
     * out of the page instead of moving the carousel — confirmed by a real drag
     * across the airbnb 50/50's media half, where `dragstart` fired.
     *
     * Suppressed only while WE are tracking a pointer, so an image the visitor
     * presses outside a swipe keeps its normal behaviour.
     */
    this.addEventListener('dragstart', (event) => {
      if (this.drag || this.dragged) event.preventDefault();
    });

    /**
     * A drag that happens to end over a link or a CTA must not also activate it.
     * Capture phase, so it is swallowed before it reaches the target's own
     * handler — the 50/50's panels are full of buttons.
     */
    this.addEventListener(
      'click',
      (event) => {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
      },
      true
    );
  }

  /* ----------------------------------------------------------------- wheel */

  /**
   * TRACKPAD SWIPE — a two-finger horizontal push, the gesture the hero already
   * answers.
   *
   * The hero gets it for nothing: its track is `overflow-x: auto` with
   * `scroll-snap-type: x mandatory`, so the browser turns `deltaX` into snapped
   * scrolling and no JS is involved. This carousel has no scroll track to give
   * that to — the panels crossfade in one shared grid cell — so the same input
   * has to be read directly and resolved to show(). Consistency across the two
   * is the point: a visitor should not have to learn which slider is which.
   *
   * Distinct from the pointer drag above, and both are needed: a drag is a
   * pressed pointer, a trackpad swipe never presses anything and emits no
   * pointer events at all.
   */
  initWheel() {
    let travel = 0;
    let locked = false;
    let idle = null;

    this.addEventListener(
      'wheel',
      (event) => {
        /**
         * Vertical intent wins, and this guard is the whole reason the page
         * still scrolls normally over a carousel: a trackpad emits a little
         * incidental deltaX on almost every vertical scroll, so acting on any
         * horizontal component at all would hijack the page.
         */
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        /**
         * Ours now. preventDefault also suppresses the browser's own
         * horizontal-overscroll gesture — on a trackpad that is BACK
         * NAVIGATION, so without this a swipe on the carousel can leave the
         * page entirely. Needs `passive: false` to be allowed.
         */
        event.preventDefault();

        clearTimeout(idle);
        idle = setTimeout(() => {
          travel = 0;
          locked = false;
        }, WHEEL_IDLE);

        // Still inside the gesture that already moved a slide — swallow the
        // momentum tail rather than running down the carousel.
        if (locked) return;

        travel += event.deltaX;
        if (Math.abs(travel) < WHEEL_THRESHOLD) return;

        // Positive deltaX is a two-finger push LEFT, i.e. the content moving
        // left — the next slide, matching the hero's scroll direction.
        const direction = travel > 0 ? 1 : -1;
        travel = 0;
        locked = true;
        this.stopAutoplay(true);
        this.show(this.index + direction);
      },
      { passive: false }
    );
  }

  /* -------------------------------------------------------------- autoplay */

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.autoplayStopped) return;
    // `visibilitychange` only fires on a CHANGE — a page opened in a background
    // tab is already hidden at connect time and would otherwise rotate unseen.
    if (document.hidden) return;
    this.pauseAutoplay();

    const seconds = parseInt(this.dataset.autoplaySpeed, 10) || 6;
    this.timer = setInterval(() => this.show(this.index + 1), seconds * 1000);
  }

  pauseAutoplay() {
    clearInterval(this.timer);
    this.timer = null;
  }

  /** @param {boolean} permanently — visitor took control; don't come back. */
  stopAutoplay(permanently) {
    this.pauseAutoplay();
    if (permanently) this.autoplayStopped = true;
  }

  resumeAutoplay() {
    if (document.hidden) return;
    this.startAutoplay();
  }

  onVisibilityChange() {
    if (document.hidden) this.pauseAutoplay();
    else this.resumeAutoplay();
  }
}

if (!customElements.get('hp-centre-carousel')) {
  customElements.define('hp-centre-carousel', HpCentreCarousel);
}

/**
 * Theme editor: selecting a block should bring its panel up rather than leaving
 * the merchant looking at a different one.
 */
document.addEventListener('shopify:block:select', (event) => {
  const carousel = event.target.closest?.('hp-centre-carousel');
  if (!carousel) return;

  carousel.stopAutoplay(true);
  carousel.show(Number(event.target.dataset.index) || 0);
});
